import { EMPTY_LIST_NAME } from "../data/store.js";
import { el } from "../lib/dom.js";
import { listPath, navigate } from "../lib/router.js";
import { actionRow, banner, kindControl, messageOf, type Popup, type Shell } from "./ui.js";

/**
 * The New list popup: what the plus opens on the index (L01, L02).
 *
 * It replaced the form that sat at the top of the index, always on screen.
 * This is one surface, opened on purpose: a labelled name, the kind as one
 * row of three, Create list and Cancel. Nothing more is invented — no rule
 * against two lists with one name, no description field.
 *
 * Nothing here waits on the network. `store.createList` returns the row
 * before anything is sent, and the next thing anyone does with a new list is
 * put something in it, so the screen goes straight there.
 */

/** Exported so the tests pin the words. */
export const DISCARD_LIST = {
  title: "Discard this list?",
  message: "What you typed will be lost.",
  keep: "Keep editing",
  discard: "Discard",
};

export function openNewList(
  shell: Shell,
  options: {
    /** Where the focus goes when the popup closes without creating: the plus. */
    returnFocusTo?: HTMLElement;
  } = {},
): Popup {
  const { store } = shell;

  const nameField = el("input", {
    attrs: {
      id: "new-list-name",
      maxlength: 200,
      enterkeyhint: "done",
      "aria-describedby": "new-list-name-error",
    },
  });
  const nameError = el("span", {
    class: "field-error",
    attrs: { id: "new-list-name-error", hidden: true },
  });
  const nameRow = el("div", {
    class: "field",
    children: [
      el("label", { class: "field-label", text: "List name", attrs: { for: "new-list-name" } }),
      nameField,
      nameError,
    ],
  });

  const kind = kindControl("todo");
  let saved = false;

  const status = el("div", { class: "popup-status" });
  const createButton = el("button", { text: "Create list", attrs: { type: "submit" } });
  const cancelButton = el("button", {
    class: "secondary",
    text: "Cancel",
    attrs: { type: "button" },
    on: { click: () => popup.dismiss() },
  });

  const clearNameError = (): void => {
    nameError.textContent = "";
    nameError.hidden = true;
    nameField.removeAttribute("aria-invalid");
  };
  nameField.addEventListener("input", clearNameError);

  /**
   * The store's own words, said beside the field rather than thrown: the
   * check here spares a round trip to a throw and a banner for the one
   * refusal a person can cause by pressing Enter too soon. Said as well as
   * shown — a description that changes under a focused field is announced by
   * nothing, and the popup's own slot is not a live region.
   */
  const refuseBlank = (): void => {
    nameError.textContent = EMPTY_LIST_NAME;
    nameError.hidden = false;
    nameField.setAttribute("aria-invalid", "true");
    shell.announce(EMPTY_LIST_NAME);
    nameField.focus();
  };

  function save(): void {
    const name = nameField.value.trim();
    if (!name) {
      refuseBlank();
      return;
    }

    let created;
    try {
      // The list exists because the store says so, not because this handler
      // made it. Nothing to disable and nothing to await.
      created = store.createList({ name, kind: kind.value() });
    } catch (error: unknown) {
      // Only a value the store will not queue reaches here — there is no
      // request to fail. Everything typed stays where it is.
      const message = messageOf(error);
      status.replaceChildren(banner(message));
      shell.announce(message);
      return;
    }

    // Straight into it, and by *replacing* the history entry rather than
    // closing the popup and pushing. The popup spent an entry on the index's
    // own URL; `popup.close()` would give it back with a `history.back()`
    // that lands later, and a push after it leaves [/, /(popup), /l/id] — a
    // Back that goes nowhere — or races the traversal. Replacing turns the
    // popup's entry into the list's: [/, /l/id], the shape the old form
    // produced. The screen that mounts for it drops this popup without
    // touching history; see `Chrome.mount`.
    saved = true;
    navigate(listPath(created.id), { replace: true });
  }

  const form = el("form", {
    class: "new-list item-form",
    attrs: { novalidate: true },
    children: [
      el("div", { class: "form-group", children: [nameRow, kind.node] }),
      status,
      actionRow([cancelButton], createButton),
    ],
    on: {
      submit: (event) => {
        event.preventDefault();
        save();
      },
    },
  });

  const popup = shell.openPopup({
    title: "New list",
    body: form,
    focus: nameField,
    returnFocusTo: options.returnFocusTo,
    guard: {
      dirty: () => !saved && (nameField.value !== "" || kind.value() !== "todo"),
      ...DISCARD_LIST,
    },
  });

  return popup;
}
